import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

const ApplicationsSection: React.FC = () => {
  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">
            Applications
          </h2>
        </motion.div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 01 AI HR */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative"
          >
            {/* HR Interface Mockup */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-8 h-48 relative overflow-hidden">
              {/* Desktop interface */}
              <div className="grid grid-cols-4 gap-2 h-full">
                {/* Left sidebar */}
                <div className="space-y-2">
                  <div className="w-full h-4 bg-gray-600 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                </div>
                
                {/* Main content area */}
                <div className="col-span-2 relative">
                  {/* Central element */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-16 h-16 bg-blue-600/20 border-2 border-blue-400 rounded-lg flex items-center justify-center">
                      <span className="text-blue-400 text-2xl">✱</span>
                    </div>
                  </div>
                </div>
                
                {/* Right sidebar */}
                <div className="space-y-2">
                  <div className="w-full h-4 bg-gray-600 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                  <div className="w-full h-4 bg-gray-700 rounded"></div>
                </div>
              </div>
            </div>

            <h3 className="text-3xl font-bold text-white mb-4">
              <span className="text-gray-500">01</span> AI HR
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Handles repetitive HR workflows, enhances productivity, and reduces manual errors — while keeping sensitive employee data private and secure. Allow simultaneously screening hundreds of applicants without human intervention.
            </p>
          </motion.div>

          {/* 02 AI Data Scientist */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative"
          >
            {/* Code Editor Mockup */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-8 h-48 relative overflow-hidden">
              {/* Code editor header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="flex space-x-2">
                  <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">HTML</span>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">React</span>
                  <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded">CSS</span>
                </div>
              </div>
              
              {/* Code content */}
              <div className="text-xs font-mono space-y-1 text-gray-300">
                <div><span className="text-gray-500">1</span> <span className="text-blue-400">&lt;html</span> <span className="text-green-400">lang=</span><span className="text-yellow-400">"en"</span><span className="text-blue-400">&gt;</span></div>
                <div><span className="text-gray-500">2</span> <span className="text-blue-400">&lt;head&gt;</span></div>
                <div><span className="text-gray-500">3</span>   <span className="text-blue-400">&lt;meta</span> <span className="text-green-400">charset=</span><span className="text-yellow-400">"UTF-8"</span><span className="text-blue-400">&gt;</span></div>
                <div><span className="text-gray-500">4</span>   <span className="text-blue-400">&lt;meta</span> <span className="text-green-400">name=</span><span className="text-yellow-400">"viewport"</span></div>
                <div><span className="text-gray-500">5</span>     <span className="text-green-400">content=</span><span className="text-yellow-400">"width=device"</span> 
                  <span className="bg-gray-700 text-white text-xs px-1 rounded ml-2">Tibor</span>
                </div>
                <div><span className="text-gray-500">6</span>   <span className="text-green-400">width:</span> <span className="text-yellow-400">initial</span></div>
              </div>
            </div>

            <h3 className="text-3xl font-bold text-white mb-4">
              <span className="text-gray-500">02</span> AI Data Scientist
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Accelerates data workflows at scale across Excel, SQL clients, browser dashboards, and notebooks — all on one shared desktop.
            </p>
            <p className="text-gray-400 text-sm">
              No code. No maintenance. Data cleaned.
            </p>
          </motion.div>

          {/* 03 AI O&G Specialist */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative"
          >
            {/* Performance Dashboard Mockup */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-8 h-48 relative overflow-hidden">
              {/* Metrics */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Software spend</span>
                  <span className="text-green-400 text-xs">+36%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Workflow efficiency</span>
                  <span className="text-green-400 text-xs">+25%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Operational cost</span>
                  <span className="text-green-400 text-xs">-11%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Update available</span>
                  <div className="flex items-center space-x-1">
                    <span className="text-blue-400 text-xs">Update</span>
                    <ArrowUp className="w-3 h-3 text-blue-400" />
                  </div>
                </div>
              </div>
              
              {/* Visual indicator */}
              <div className="absolute bottom-4 right-4">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
              </div>
            </div>

            <h3 className="text-3xl font-bold text-white mb-4">
              <span className="text-gray-500">03</span> AI O&G Specialist
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Enhances field-to-office workflows, automates data entry from sensor reports, and supports engineering tasks across legacy software.
            </p>
            <p className="text-gray-400 text-sm">
              No integration. No blackbox.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationsSection;